/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import SumsubWebSdk from '@sumsub/websdk-react';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import { setUnderReviewStatus } from 'services/requestAgent';
import userStore from 'stores/userStore';
import Alert from 'uikit/Alert/Alert';
import { Container } from 'uikit/Container/Container';
import { trackTelemetryEvent, TELEMETRY_EVENTS } from 'services/telemetry';
import './Verification.scss';

const MAX_VERIFICATION_START_ATTEMPTS = 3;
const VERIFICATION_START_RETRY_DELAY_MS = 1500;
// TRN-7621: no level configured in BO for the customer's KYC profile and none sent by the frontend - retrying
// won't help, the operator has to set the level.
const KYC_LEVEL_REQUIRED = 'KYC_LEVEL_REQUIRED';

const Sumsub = ({ email, phone, customerNumber, levelName, accessToken, error, generateToken }) => {
  const i18n = useContext(i18nContext);

  useEffect(() => {
    let cancelled = false;

    // Right after a customer is created, the KYC session on the provider's side can take a moment
    // to become available, so the initial generateToken() call can transiently fail. Since this
    // effect only ever runs once, a failed attempt would otherwise leave the widget blank forever -
    // retry a few times with a short delay before giving up.
    const startVerificationWithRetry = async (attempt) => {
      await generateToken(customerNumber, levelName);
      if (cancelled || userStore.verificationAccessToken || userStore.error?.code === KYC_LEVEL_REQUIRED) return;
      if (attempt < MAX_VERIFICATION_START_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, VERIFICATION_START_RETRY_DELAY_MS));
        if (!cancelled) await startVerificationWithRetry(attempt + 1);
      }
    };

    startVerificationWithRetry(0);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (accessToken) {
      trackTelemetryEvent(TELEMETRY_EVENTS.CUSTOMER_VERIFICATION_START, { customerNumber });
    }
  }, [accessToken]);

  // TRN-7621: the SDK's expirationHandler expects the new token back.
  const getAccessToken = async () => {
    await generateToken(customerNumber, levelName);
    return userStore.verificationAccessToken;
  };

  const handleOnMessage = async (type) => {
    if (type === 'idCheck.onApplicantSubmitted') {
      await setUnderReviewStatus(customerNumber);
    }
  };

  return (
    <section className='right-section'>
      <Container className='sumsub-verification-container' header={i18n.getMessage('container.verification')}>
        <div className='sumsub'>
          {!accessToken && error?.code === KYC_LEVEL_REQUIRED && (
            <Alert
              type={'warning'}
              message={
                <>
                  {i18n.getMessage('verification.sumsub.levelRequired.message')}
                  <a href={ROUTE_PATHS.MESSAGES} className='link-normal'>
                    {i18n.getMessage('verification.sumsub.levelRequired.support')}
                  </a>
                </>
              }
            />
          )}
          {accessToken && (
            <SumsubWebSdk
              accessToken={accessToken}
              expirationHandler={getAccessToken}
              config={{
                email: email,
                phone: phone
              }}
              onMessage={handleOnMessage}
            />
          )}
        </div>
      </Container>
    </section>
  );
};

Sumsub.propTypes = {
  email: PropTypes.string,
  customerNumber: PropTypes.string,
  levelName: PropTypes.string,
  accessToken: PropTypes.string,
  error: PropTypes.object,
  generateToken: PropTypes.func
};

export default Sumsub;
